<template>
  <v-dialog :value="value" max-width="680" scrollable @input="$emit('input', $event)">
    <v-card v-if="targets.length">
      <v-card-title class="text-subtitle-1 font-weight-medium">
        <template v-if="single">
          Подходящая номенклатура — {{ first.groupTitle }} · {{ first.label }}
          <span v-if="first.elementName !== first.label" class="grey--text ml-2 font-weight-regular">{{
            first.elementName
          }}</span>
        </template>
        <template v-else>
          Подходящая номенклатура — {{ pluralSlotsText }}
          <span class="grey--text ml-2 font-weight-regular">{{ first.elementName }}</span>
        </template>
      </v-card-title>

      <v-card-text>
        <!-- как произошёл подбор: семейство, условия (одинаковые или по группам слотов) и итог -->
        <div v-if="showExplain" class="module_scheme_slot_picker__explain">
          <div class="module_scheme_slot_picker__explain_title">Как подобрано</div>
          <ul>
            <li v-if="explain.family">
              Семейство <b>«{{ explain.family.name }}»</b> (по элементу «{{ first.elementName }}») — в каталоге
              {{ positions(explain.family.total) }}
            </li>
            <li v-if="explain.groups.length === 1">
              {{ single ? "Условия слота" : `Условия одинаковые у всех ${targets.length} слотов` }}:
              <v-chip v-for="text in explain.groups[0].rules" :key="text" x-small label class="mr-1">{{ text }}</v-chip>
            </li>
            <li v-else>
              Условия у слотов разные ({{ explain.groups.length }} {{ groupsWord }}) — предложены позиции, которые
              проходят условия каждой группы:
              <ul class="module_scheme_slot_picker__groups">
                <li v-for="(group, i) in explain.groups" :key="i">
                  <v-chip v-for="text in group.rules" :key="text" x-small label class="mr-1">{{ text }}</v-chip>
                  — {{ slotsText(group.targets) }}: {{ positions(group.own) }}
                </li>
              </ul>
            </li>
            <li>
              Итог: <b>{{ resultText }}</b>
            </li>
          </ul>
        </div>

        <div v-if="loading" class="text-center py-6">
          <v-progress-circular indeterminate size="28"></v-progress-circular>
        </div>

        <!-- пусто: только те причины, которые бэк определил по выбранному оборудованию -->
        <div v-else-if="!items.length" class="text-caption">
          <div class="mb-2">Ничего не подходит:</div>
          <ul class="module_scheme_slot_picker__hints">
            <li v-for="problem in problems" :key="problem.code">
              <template v-if="problem.code === 'no_conditions'">
                <b>Нет условий подбора</b> — задайте их в карточке узла («Оборудование»):
              </template>
              <template v-else-if="problem.code === 'no_family'">
                <b>Не задано семейство номенклатуры</b> у элемента «{{ first.elementName }}» — заполните его
                <a :href="legendLink(first.idElement)" target="_blank" rel="noopener"
                  >в «Легенде узлов отопления — элементы»</a
                >
                (обязательно: подбор идёт только по семейству)
              </template>
              <template v-else-if="problem.code === 'no_matches'">
                {{ problem.message }}. Измените условия в карточке узла или добавьте подходящую номенклатуру в
                каталог:
              </template>
              <template v-else>{{ problem.message }}</template>

              <!-- узлы и слоты, которых касается проблема: узел — ссылка на карточку, под ним слоты столбиком -->
              <ul v-if="showsNodeList(problem)" class="module_scheme_slot_picker__nodes">
                <li v-for="node in nodeGroups(problem)" :key="node.idNode">
                  <a :href="nodeLink(node.idNode)" target="_blank" rel="noopener">{{ node.title }}</a
                  >:
                  <ul>
                    <li v-for="label in node.labels" :key="label">{{ label }}</li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </div>

        <!-- одна позиция из подходящих: клик отмечает, «Присоединить» записывает в слоты -->
        <v-list v-else dense>
          <template v-for="[groupName, groupItems] in groupedItems">
            <v-subheader :key="`h-${groupName}`">{{ groupName }}</v-subheader>
            <v-list-item
              v-for="item in groupItems"
              :key="item.id"
              :disabled="saving"
              :class="{ module_scheme_slot_picker__picked: pending === item.id }"
              @click="pending = item.id"
            >
              <v-list-item-icon class="mr-3">
                <v-icon :color="pending === item.id ? 'primary' : undefined">
                  {{ pending === item.id ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="item.id === initialPending">
                <v-chip x-small color="green" text-color="white">присоединено</v-chip>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list>
      </v-card-text>

      <v-card-actions>
        <v-btn v-if="hasChosen" text color="red" :disabled="saving" @click="$emit('clear')">
          Убрать позицию
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('input', false)">Закрыть</v-btn>
        <v-btn color="primary" text :loading="saving" :disabled="!canAttach" @click="attach">
          Присоединить
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { formatRule, pluralPositions, pluralSlots } from "@/helpers/moduleSchemeSlots";

/**
 * Подбор номенклатуры для одного слота или для нескольких слотов одного типа сразу
 * (массовое назначение: «один кран на все КШ»). Для нескольких слотов по каждому уникальному
 * (узел, экземпляр) спрашиваем кандидатов у бэка и оставляем позиции, которые подходят всем.
 * Записью в БД занимается родитель: окно только отдаёт выбор (attach) или снятие выбора (clear).
 */
export default {
  name: "ModuleSchemeSlotPicker",
  props: {
    value: { type: Boolean, default: false },
    // выбранные слоты (строки из buildSlotGroups) — все одного типа элемента
    targets: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
  },
  data: () => ({
    loading: false,
    items: [],
    // как произошёл подбор: семейство, группы слотов с одинаковыми условиями, число подошедших
    explain: null,
    problems: [],
    pending: null,
    initialPending: null,
    // ответ устаревает, если окно закрыли или открыли для других слотов
    requestId: 0,
  }),
  computed: {
    first() {
      return this.targets[0] || {};
    },
    single() {
      return this.targets.length === 1;
    },
    pluralSlotsText() {
      return pluralSlots(this.targets.length);
    },
    // пояснение показываем, когда подбор состоялся или дошёл до сравнения с каталогом; причины «нет условий /
    // нет семейства / разные типы» объясняет список проблем ниже
    showExplain() {
      if (this.loading || !this.explain || !this.explain.groups.length) return false;
      return !this.problems.some((p) => ["no_conditions", "no_family", "mixed"].includes(p.code));
    },
    groupsWord() {
      const n = this.explain ? this.explain.groups.length : 0;
      return n >= 2 && n <= 4 ? "группы" : "групп";
    },
    resultText() {
      const found = this.items.length;
      if (!found) return "подходящих позиций нет";
      const total = this.explain && this.explain.family ? ` из ${this.explain.family.total}` : "";
      return this.single ? `подходит ${found}${total}` : `общих для всех ${this.targets.length} слотов: ${found}${total}`;
    },
    hasChosen() {
      return this.targets.some((t) => t.chosen);
    },
    groupedItems() {
      const map = new Map();
      this.items.forEach((item) => {
        const key = item.group || "Без категории";
        map.set(key, [...(map.get(key) || []), item]);
      });
      return [...map.entries()];
    },
    // «Присоединить» доступна, когда отмечена позиция, отличная от уже присоединённой
    canAttach() {
      return this.pending !== null && !this.saving && this.pending !== this.initialPending;
    },
  },
  watch: {
    value(open) {
      if (open) this.init();
      else this.requestId++;
    },
  },
  methods: {
    // карточка узла каталога и справочник элементов — в новой вкладке, чтобы не потерять окно подбора
    nodeLink(idNode) {
      return this.$router.resolve({ path: "/scheme-nodes-tree", query: { open: String(idNode) } }).href;
    },
    // форма самого элемента с курсором в поле «Семейство»
    legendLink(idElement) {
      return this.$router.resolve({
        path: "/scheme-node-elements",
        query: { element: String(idElement), focus: "family" },
      }).href;
    },
    positions: pluralPositions,
    // слоты группы по модулям: «ВР-03: КШ-1, КШ-2; PG-M1: КШ-1»
    slotsText(targets) {
      const byNode = new Map();
      targets.forEach((t) => byNode.set(t.groupTitle, [...(byNode.get(t.groupTitle) || []), t.label]));
      return [...byNode.entries()].map(([title, labels]) => `${title}: ${labels.join(", ")}`).join("; ");
    },
    // проблемы, которые решаются правкой условий в карточке узла: показываем узлы и их слоты
    showsNodeList(problem) {
      return problem.code === "no_conditions" || problem.code === "no_matches";
    },
    // слоты, которых касается проблема, по узлам: условия задаются у узла каталога, поэтому одинаковые
    // слоты разных размещений одного узла («КШ-2» в двух модулях ВР-03) сводим в один
    nodeGroups(problem) {
      const groups = new Map();
      problem.targets.forEach((target) => {
        const group = groups.get(target.idNode) || {
          idNode: target.idNode,
          title: target.groupTitle.replace(/ \(\d+\)$/, ""),
          labels: [],
        };
        if (!group.labels.includes(target.label)) group.labels.push(target.label);
        groups.set(target.idNode, group);
      });
      return [...groups.values()];
    },
    async init() {
      const requestId = ++this.requestId;
      this.items = [];
      this.explain = null;
      this.problems = [];
      this.loading = true;

      // уже присоединённая позиция сразу отмечена; у нескольких слотов — только если у всех одна
      const chosenIds = [...new Set(this.targets.map((t) => (t.chosen ? t.chosen.id : null)))];
      this.initialPending = chosenIds.length === 1 && chosenIds[0] ? chosenIds[0] : null;
      this.pending = this.initialPending;

      // один запрос на уникальный (узел, экземпляр): одинаковые модули не спрашиваем дважды
      const requests = [];
      const seen = new Set();
      this.targets.forEach((t) => {
        const id = `${t.idNode}|${t.key}`;
        if (seen.has(id)) return;
        seen.add(id);
        requests.push(t);
      });
      const results = await Promise.all(
        requests.map(async (t) => ({
          request: t,
          ...(await this.$store.dispatch("ModuleSchemesModule/loadSlotCandidates", { idNode: t.idNode, key: t.key })),
        }))
      );
      if (requestId !== this.requestId) return;

      this.aggregate(results);
      this.loading = false;
    },
    // Пересечение кандидатов по всем запросам, пояснение «как подобрано» и причины «почему пусто»
    aggregate(results) {
      // группы слотов с одинаковыми условиями: у одной группы одинаковые условия, значит и одинаковый список
      const bySignature = new Map();
      results.forEach((r) => {
        // порядок условий для показа — как заданы в узле («≥ 20, ≤ 40»); для сравнения групп — отсортированный
        const texts = r.rules.map(formatRule);
        const key = [...texts].sort().join("|");
        const group = bySignature.get(key) || { rules: texts, targets: [], own: r.items.length };
        this.targets
          .filter((t) => t.idNode === r.request.idNode && t.key === r.request.key)
          .forEach((t) => group.targets.push(t));
        bySignature.set(key, group);
      });
      const family = (results.find((r) => r.family) || {}).family || null;
      this.explain = { family, groups: [...bySignature.values()].filter((g) => g.rules.length) };

      // причины по коду; слоты, которых они касаются, — все цели с таким же (узел, экземпляр)
      const problems = new Map();
      results.forEach((r) => {
        r.problems.forEach((problem) => {
          const entry = problems.get(problem.code) || { code: problem.code, message: problem.message, targets: [] };
          this.targets
            .filter((t) => t.idNode === r.request.idNode && t.key === r.request.key)
            .forEach((t) => entry.targets.push(t));
          problems.set(problem.code, entry);
        });
      });

      if (problems.size) {
        this.problems = [...problems.values()];
        this.items = [];
        return;
      }

      // пересечение: позиция должна подойти каждому слоту
      let common = results[0] ? results[0].items : [];
      results.slice(1).forEach((r) => {
        const ids = new Set(r.items.map((item) => item.id));
        common = common.filter((item) => ids.has(item.id));
      });
      this.items = common;

      if (!common.length && results.length > 1) {
        this.problems = [
          {
            code: "no_common",
            message: "Нет позиции, подходящей сразу всем выбранным слотам — выберите меньше слотов или измените условия",
            targets: this.targets,
          },
        ];
      }
    },
    attach() {
      const item = this.items.find((i) => i.id === this.pending);
      if (!item) return;
      // количество пока не задаём (null): у нового слота бэк ставит 1, у существующего не меняет
      this.$emit("attach", { idNomenclature: item.id, name: item.name, quantity: null });
    },
  },
};
</script>

<style lang="scss" scoped>
.module_scheme_slot_picker {
  &__explain {
    margin-bottom: 10px;
    padding: 8px 12px;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.04);
    font-size: 0.8em;
    ul {
      padding-left: 18px;
    }
    li + li {
      margin-top: 4px;
    }
  }
  &__explain_title {
    margin-bottom: 4px;
    font-size: 0.85em;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.6);
  }
  &__groups {
    margin-top: 4px;
  }
  &__hints {
    padding-left: 18px;
    > li + li {
      margin-top: 8px;
    }
  }
  &__nodes {
    margin-top: 4px;
    padding-left: 16px;
    ul {
      padding-left: 18px;
    }
  }
  &__picked {
    background: rgba(76, 175, 80, 0.12);
  }
}
</style>
